import {
  V2_DEFAULT_DAILY_TARGETS,
  V2_DEFAULT_DAILY_SYSTEM_REMINDER,
  V2_DEFAULT_ADMIN_APP_SETTINGS,
  V2_DEFAULT_PROFILE,
  V2_DEFAULT_REMINDERS,
  V2_DEFAULT_SETTINGS,
  V2_DEFAULT_VISIBILITY,
  V2_ALLOWED_LANGUAGES,
  V2_ALLOWED_REMINDER_CHANNELS,
  V2_ALLOWED_THEMES,
  V2_LEGACY_STORAGE_KEYS,
  V2_STORAGE_KEYS,
} from "./v2Schema";
import { V2_MAIN_FOODS } from "./v2FoodData";
import { buildUnifiedFoodList, prepareCustomFoodPayload } from "./v2FoodUtils";
import { createFoodLogSnapshot, sortLogsNewestFirst } from "./v2FoodLogUtils";
import { calculateProteinLimitFromWeight, isValidWeightKg } from "./v2NutritionUtils";
import {
  getReminderTimes,
  normalizeTimesPerDay,
  prepareMedicationReminderPayload,
  prepareReminderEventPayload,
} from "./v2ReminderUtils";
import { APPROVED_DASHBOARD_TIPS, DASHBOARD_QUOTES } from "./v2DashboardContent";
import {
  createAuditLogEntry,
  mergeAdminAppSettings,
  prepareAdminMainFoodPayload,
  prepareApprovedTipPayload,
  prepareKnowledgeItemPayload,
  prepareQuotePayload,
} from "./v2AdminUtils";

function isAllowed(value, allowedValues, fallback) {
  return allowedValues.includes(value) ? value : fallback;
}

function normalizeProfile(profile = {}) {
  const weightKg = profile.weight_kg ?? profile.weight ?? V2_DEFAULT_PROFILE.weight_kg;

  return {
    ...V2_DEFAULT_PROFILE,
    ...profile,
    weight: profile.weight ?? weightKg,
    weight_kg: weightKg,
    language: isAllowed(profile.language, V2_ALLOWED_LANGUAGES, V2_DEFAULT_PROFILE.language),
    theme: isAllowed(profile.theme, V2_ALLOWED_THEMES, V2_DEFAULT_PROFILE.theme),
    reminder_channel: isAllowed(
      profile.reminder_channel,
      V2_ALLOWED_REMINDER_CHANNELS,
      V2_DEFAULT_PROFILE.reminder_channel
    ),
  };
}

function normalizeReminders(reminders = {}, profile = {}) {
  const channel = reminders.channel ?? profile.reminder_channel ?? V2_DEFAULT_REMINDERS.channel;
  const dailySystemReminder = reminders.daily_system_reminder || {};

  return {
    ...V2_DEFAULT_REMINDERS,
    enabled: reminders.enabled ?? profile.reminders_enabled ?? V2_DEFAULT_REMINDERS.enabled,
    notifications_enabled:
      reminders.notifications_enabled ??
      profile.notifications_enabled ??
      V2_DEFAULT_REMINDERS.notifications_enabled,
    push_notifications_enabled:
      reminders.push_notifications_enabled ??
      profile.push_notifications_enabled ??
      V2_DEFAULT_REMINDERS.push_notifications_enabled,
    whatsapp_number:
      reminders.whatsapp_number ?? profile.whatsapp_number ?? V2_DEFAULT_REMINDERS.whatsapp_number,
    whatsapp_reminders_enabled:
      reminders.whatsapp_reminders_enabled ??
      profile.whatsapp_reminders_enabled ??
      V2_DEFAULT_REMINDERS.whatsapp_reminders_enabled,
    whatsapp_consent:
      reminders.whatsapp_consent ?? profile.whatsapp_consent ?? V2_DEFAULT_REMINDERS.whatsapp_consent,
    whatsapp_include_medication_name:
      reminders.whatsapp_include_medication_name ??
      profile.whatsapp_include_medication_name ??
      V2_DEFAULT_REMINDERS.whatsapp_include_medication_name,
    channel: isAllowed(channel, V2_ALLOWED_REMINDER_CHANNELS, V2_DEFAULT_REMINDERS.channel),
    daily_system_reminder: {
      ...V2_DEFAULT_DAILY_SYSTEM_REMINDER,
      ...dailySystemReminder,
      time: dailySystemReminder.time || V2_DEFAULT_DAILY_SYSTEM_REMINDER.time,
      message: dailySystemReminder.message || V2_DEFAULT_DAILY_SYSTEM_REMINDER.message,
      enabled: Boolean(dailySystemReminder.enabled),
    },
  };
}

function normalizeDailyTargets(settings = {}, profile = {}) {
  const dailyTargets = settings.daily_targets || {};
  const dailyLimits = settings.daily_limits || {};
  const visibility = settings.visibility || {};
  const calculatedProteinLimit = isValidWeightKg(profile.weight_kg)
    ? calculateProteinLimitFromWeight(profile.weight_kg)
    : null;

  return {
    ...V2_DEFAULT_DAILY_TARGETS,
    ...dailyTargets,
    potassium_limit_mg:
      dailyLimits.potassium_mg ??
      dailyTargets.potassium_limit_mg ??
      V2_DEFAULT_DAILY_TARGETS.potassium_limit_mg,
    phosphorus_limit_mg:
      dailyLimits.phosphorus_mg ??
      dailyTargets.phosphorus_limit_mg ??
      V2_DEFAULT_DAILY_TARGETS.phosphorus_limit_mg,
    sodium_limit_mg:
      dailyLimits.sodium_mg ?? dailyTargets.sodium_limit_mg ?? V2_DEFAULT_DAILY_TARGETS.sodium_limit_mg,
    protein_limit_g:
      calculatedProteinLimit ??
      dailyLimits.protein_g ??
      dailyTargets.protein_limit_g ??
      V2_DEFAULT_DAILY_TARGETS.protein_limit_g,
    show_weight_fluid_card:
      visibility.weight_fluid_card ??
      dailyTargets.show_weight_fluid_card ??
      V2_DEFAULT_DAILY_TARGETS.show_weight_fluid_card,
  };
}

function dailyTargetsToLegacyLimits(dailyTargets = {}) {
  return {
    potassium_mg: dailyTargets.potassium_limit_mg ?? "",
    phosphorus_mg: dailyTargets.phosphorus_limit_mg ?? "",
    sodium_mg: dailyTargets.sodium_limit_mg ?? "",
    protein_g: dailyTargets.protein_limit_g ?? "",
  };
}

export function mergeV2Settings(settings = {}) {
  const profile = normalizeProfile(settings.profile || {});
  const reminders = normalizeReminders(settings.reminders || {}, profile);
  const dailyTargets = normalizeDailyTargets(settings, profile);
  const dailyLimits = {
    ...V2_DEFAULT_SETTINGS.daily_limits,
    ...(settings.daily_limits || {}),
    ...dailyTargetsToLegacyLimits(dailyTargets),
  };
  const visibility = {
    ...V2_DEFAULT_VISIBILITY,
    ...(settings.visibility || {}),
    weight_fluid_card: dailyTargets.show_weight_fluid_card,
  };

  return {
    profile,
    reminders,
    daily_limits: dailyLimits,
    daily_targets: dailyTargets,
    visibility,
  };
}

export function readV2Json(key, fallback) {
  if (typeof window === "undefined") return fallback;

  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    window.localStorage.removeItem(key);
    return fallback;
  }
}

export function writeV2Json(key, value) {
  if (typeof window === "undefined") return value;
  window.localStorage.setItem(key, JSON.stringify(value));
  return value;
}

export function removeV2Json(key) {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(key);
}

export function loadV2Session() {
  return readV2Json(V2_STORAGE_KEYS.session, null);
}

export function saveV2Session(session) {
  return writeV2Json(V2_STORAGE_KEYS.session, session);
}

export function clearV2Session() {
  removeV2Json(V2_STORAGE_KEYS.session);
  removeV2Json(V2_LEGACY_STORAGE_KEYS.guestMode);
  removeV2Json(V2_LEGACY_STORAGE_KEYS.guestProfile);
}

export function loadV2Settings() {
  return mergeV2Settings(readV2Json(V2_STORAGE_KEYS.settings, {}));
}

export function saveV2Settings(settings) {
  return writeV2Json(V2_STORAGE_KEYS.settings, mergeV2Settings(settings));
}

export function loadV2Profile() {
  return loadV2Settings().profile;
}

export function saveV2Profile(profile) {
  const currentSettings = loadV2Settings();
  return saveV2Settings({
    ...currentSettings,
    profile: {
      ...currentSettings.profile,
      ...profile,
    },
  }).profile;
}

export function loadV2DailyTargets() {
  return loadV2Settings().daily_targets;
}

export function loadV2FoodLogs({ isGuest = false } = {}) {
  const key = isGuest ? V2_STORAGE_KEYS.guestFoodLogs : V2_STORAGE_KEYS.foodLogs;
  return sortLogsNewestFirst(getV2GuestCollection(key));
}

export function saveV2FoodLogs(logs, { isGuest = false } = {}) {
  const key = isGuest ? V2_STORAGE_KEYS.guestFoodLogs : V2_STORAGE_KEYS.foodLogs;
  return saveV2GuestCollection(key, sortLogsNewestFirst(Array.isArray(logs) ? logs : []));
}

export function createV2FoodLog(food, quantity, { isGuest = false, userId = "", date } = {}) {
  const snapshot = createFoodLogSnapshot(food, quantity, { userId, date });
  if (!snapshot.ok) {
    return snapshot;
  }

  const log = {
    ...snapshot.log,
    id: createLocalId("food-log"),
  };
  const currentLogs = loadV2FoodLogs({ isGuest });
  saveV2FoodLogs([log, ...currentLogs], { isGuest });

  return { ok: true, errors: {}, log };
}

export function deleteV2FoodLog(logId, { isGuest = false } = {}) {
  const currentLogs = loadV2FoodLogs({ isGuest });
  const nextLogs = currentLogs.filter((log) => log.id !== logId);

  if (nextLogs.length === currentLogs.length) {
    return { ok: false, deletedId: null };
  }

  saveV2FoodLogs(nextLogs, { isGuest });
  return { ok: true, deletedId: logId };
}

export function loadV2MedicationReminders({ isGuest = false } = {}) {
  const key = getReminderStorageKey(isGuest);
  return getV2GuestCollection(key).map(normalizeStoredReminder).sort(sortRemindersByTime);
}

export function saveV2MedicationReminders(reminders, { isGuest = false } = {}) {
  const key = getReminderStorageKey(isGuest);
  return saveV2GuestCollection(key, Array.isArray(reminders) ? reminders.map(normalizeStoredReminder) : []);
}

export function createV2MedicationReminder(input, { isGuest = false } = {}) {
  const { validation, payload } = prepareMedicationReminderPayload(input);
  if (!validation.valid) {
    return { ok: false, errors: validation.errors, reminder: null };
  }

  const now = new Date().toISOString();
  const reminder = {
    ...payload,
    id: createLocalId("med-reminder"),
    created_at: now,
    updated_at: now,
  };
  const currentReminders = loadV2MedicationReminders({ isGuest });
  saveV2MedicationReminders([reminder, ...currentReminders], { isGuest });

  return { ok: true, errors: {}, reminder };
}

export function updateV2MedicationReminder(reminderId, input, { isGuest = false } = {}) {
  const { validation, payload } = prepareMedicationReminderPayload(input);
  if (!validation.valid) {
    return { ok: false, errors: validation.errors, reminder: null };
  }

  const currentReminders = loadV2MedicationReminders({ isGuest });
  let updatedReminder = null;
  const nextReminders = currentReminders.map((reminder) => {
    if (reminder.id !== reminderId) return reminder;

    updatedReminder = {
      ...reminder,
      ...payload,
      id: reminder.id,
      created_at: reminder.created_at,
      updated_at: new Date().toISOString(),
    };
    return updatedReminder;
  });

  if (!updatedReminder) {
    return { ok: false, errors: { id: "Reminder was not found." }, reminder: null };
  }

  saveV2MedicationReminders(nextReminders, { isGuest });
  return { ok: true, errors: {}, reminder: updatedReminder };
}

export function deleteV2MedicationReminder(reminderId, { isGuest = false } = {}) {
  const currentReminders = loadV2MedicationReminders({ isGuest });
  const nextReminders = currentReminders.filter((reminder) => reminder.id !== reminderId);

  if (nextReminders.length === currentReminders.length) {
    return { ok: false, deletedId: null };
  }

  saveV2MedicationReminders(nextReminders, { isGuest });
  return { ok: true, deletedId: reminderId };
}

export function setV2MedicationReminderActive(reminderId, active, { isGuest = false } = {}) {
  const currentReminders = loadV2MedicationReminders({ isGuest });
  let updatedReminder = null;
  const nextReminders = currentReminders.map((reminder) => {
    if (reminder.id !== reminderId) return reminder;

    updatedReminder = {
      ...reminder,
      active: Boolean(active),
      updated_at: new Date().toISOString(),
    };
    return updatedReminder;
  });

  if (!updatedReminder) {
    return { ok: false, errors: { id: "Reminder was not found." }, reminder: null };
  }

  saveV2MedicationReminders(nextReminders, { isGuest });
  return { ok: true, errors: {}, reminder: updatedReminder };
}

export function loadV2ReminderEvents({ isGuest = false } = {}) {
  const key = getReminderEventStorageKey(isGuest);
  return getV2GuestCollection(key).sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime());
}

export function saveV2ReminderEvents(events, { isGuest = false } = {}) {
  const key = getReminderEventStorageKey(isGuest);
  return saveV2GuestCollection(key, Array.isArray(events) ? events : []);
}

export function createV2ReminderEvent(
  reminder,
  status,
  {
    isGuest = false,
    now,
    scheduledFor,
    whatsappStatus = "",
    whatsappAttemptedAt = "",
    whatsappProvider = "",
    whatsappErrorMessage = "",
  } = {}
) {
  const result = prepareReminderEventPayload(reminder, status, {
    now,
    scheduledFor,
    whatsappStatus,
    whatsappAttemptedAt,
    whatsappProvider,
    whatsappErrorMessage,
  });
  if (!result.ok) {
    return result;
  }

  const event = {
    ...result.event,
    id: createLocalId("reminder-event"),
  };
  const currentEvents = loadV2ReminderEvents({ isGuest });
  saveV2ReminderEvents([event, ...currentEvents], { isGuest });

  return { ok: true, errors: {}, event };
}

export function loadV2MainFoods() {
  return loadV2AdminMainFoods().filter((food) => food.active !== false);
}

export function loadV2AdminMainFoods() {
  const savedFoods = getV2GuestCollection(V2_STORAGE_KEYS.adminMainFoods);
  return savedFoods.length > 0 ? savedFoods.map(normalizeAdminMainFood) : V2_MAIN_FOODS.map(normalizeAdminMainFood);
}

export function saveV2AdminMainFoods(foods) {
  return saveV2GuestCollection(V2_STORAGE_KEYS.adminMainFoods, Array.isArray(foods) ? foods.map(normalizeAdminMainFood) : []);
}

export function createV2AdminMainFood(input) {
  const { validation, payload } = prepareAdminMainFoodPayload(input);
  if (!validation.valid) {
    return { ok: false, errors: validation.errors, food: null };
  }

  const now = new Date().toISOString();
  const food = {
    ...payload,
    id: createLocalId("main-food"),
    created_at: now,
    updated_at: now,
  };
  saveV2AdminMainFoods([food, ...loadV2AdminMainFoods()]);
  createV2AdminAuditLog({
    action: "add food",
    targetType: "main_food",
    targetId: food.id,
    details: food.name_en,
  });

  return { ok: true, errors: {}, food };
}

export function updateV2AdminMainFood(foodId, input) {
  const { validation, payload } = prepareAdminMainFoodPayload(input);
  if (!validation.valid) {
    return { ok: false, errors: validation.errors, food: null };
  }

  let updatedFood = null;
  const nextFoods = loadV2AdminMainFoods().map((food) => {
    if (food.id !== foodId) return food;

    updatedFood = {
      ...food,
      ...payload,
      id: food.id,
      created_at: food.created_at,
      updated_at: new Date().toISOString(),
    };
    return updatedFood;
  });

  if (!updatedFood) {
    return { ok: false, errors: { id: "Food was not found." }, food: null };
  }

  saveV2AdminMainFoods(nextFoods);
  createV2AdminAuditLog({
    action: "update food",
    targetType: "main_food",
    targetId: updatedFood.id,
    details: updatedFood.name_en,
  });

  return { ok: true, errors: {}, food: updatedFood };
}

export function setV2AdminMainFoodActive(foodId, active) {
  let updatedFood = null;
  const nextFoods = loadV2AdminMainFoods().map((food) => {
    if (food.id !== foodId) return food;

    updatedFood = {
      ...food,
      active: Boolean(active),
      updated_at: new Date().toISOString(),
    };
    return updatedFood;
  });

  if (!updatedFood) {
    return { ok: false, errors: { id: "Food was not found." }, food: null };
  }

  saveV2AdminMainFoods(nextFoods);
  createV2AdminAuditLog({
    action: "update food",
    targetType: "main_food",
    targetId: updatedFood.id,
    details: updatedFood.active ? "activated" : "deactivated",
  });

  return { ok: true, errors: {}, food: updatedFood };
}

export function loadV2FoodsUnified({ isGuest = false } = {}) {
  return buildUnifiedFoodList(loadV2MainFoods(), loadV2CustomFoods({ isGuest }));
}

export function loadV2CustomFoods({ isGuest = false } = {}) {
  const key = getCustomFoodStorageKey(isGuest);
  return getV2GuestCollection(key);
}

export function saveV2CustomFoods(items, { isGuest = false } = {}) {
  const key = getCustomFoodStorageKey(isGuest);
  return saveV2GuestCollection(key, items);
}

export function createV2CustomFood(input, { isGuest = false } = {}) {
  const { validation, payload } = prepareCustomFoodPayload(input);
  if (!validation.valid) {
    return { ok: false, errors: validation.errors, food: null };
  }

  const now = new Date().toISOString();
  const food = {
    ...payload,
    id: createLocalId("custom-food"),
    created_at: now,
    updated_at: now,
  };
  const currentFoods = loadV2CustomFoods({ isGuest });
  saveV2CustomFoods([food, ...currentFoods], { isGuest });

  return { ok: true, errors: {}, food };
}

export function updateV2CustomFood(foodId, input, { isGuest = false } = {}) {
  const { validation, payload } = prepareCustomFoodPayload(input);
  if (!validation.valid) {
    return { ok: false, errors: validation.errors, food: null };
  }

  const currentFoods = loadV2CustomFoods({ isGuest });
  let updatedFood = null;
  const nextFoods = currentFoods.map((food) => {
    if (food.id !== foodId) return food;

    updatedFood = {
      ...food,
      ...payload,
      id: food.id,
      created_at: food.created_at,
      updated_at: new Date().toISOString(),
    };
    return updatedFood;
  });

  if (!updatedFood) {
    return { ok: false, errors: { id: "Custom food was not found." }, food: null };
  }

  saveV2CustomFoods(nextFoods, { isGuest });
  return { ok: true, errors: {}, food: updatedFood };
}

export function deleteV2CustomFood(foodId, { isGuest = false } = {}) {
  const currentFoods = loadV2CustomFoods({ isGuest });
  const nextFoods = currentFoods.filter((food) => food.id !== foodId);

  if (nextFoods.length === currentFoods.length) {
    return { ok: false, deletedId: null };
  }

  saveV2CustomFoods(nextFoods, { isGuest });
  return { ok: true, deletedId: foodId };
}

export function saveV2DailyTargets(dailyTargets) {
  const currentSettings = loadV2Settings();
  const nextDailyTargets = {
    ...currentSettings.daily_targets,
    ...dailyTargets,
  };

  return saveV2Settings({
    ...currentSettings,
    daily_limits: dailyTargetsToLegacyLimits(nextDailyTargets),
    daily_targets: nextDailyTargets,
    visibility: {
      ...currentSettings.visibility,
      weight_fluid_card: nextDailyTargets.show_weight_fluid_card,
    },
  }).daily_targets;
}

export function loadV2ApprovedTips({ includeInactive = false } = {}) {
  const savedTips = getV2GuestCollection(V2_STORAGE_KEYS.approvedTips);
  const tips = savedTips.length > 0 ? savedTips.map(normalizeApprovedTip) : getDefaultApprovedTips();
  return includeInactive ? tips : tips.filter((tip) => tip.active !== false && tip.reviewed);
}

export function getV2DashboardTips() {
  const appSettings = loadV2AdminAppSettings();
  if (appSettings.dashboard_tip_source === "default") {
    return getDefaultApprovedTips();
  }

  return loadV2ApprovedTips();
}

export function saveV2ApprovedTips(tips) {
  return saveV2GuestCollection(V2_STORAGE_KEYS.approvedTips, Array.isArray(tips) ? tips.map(normalizeApprovedTip) : []);
}

export function createV2ApprovedTip(input) {
  const { validation, payload } = prepareApprovedTipPayload(input);
  if (!validation.valid) return { ok: false, errors: validation.errors, tip: null };

  const now = new Date().toISOString();
  const tip = {
    ...payload,
    id: createLocalId("tip"),
    created_at: now,
    updated_at: now,
  };
  saveV2ApprovedTips([tip, ...loadV2ApprovedTips({ includeInactive: true })]);
  createV2AdminAuditLog({ action: "add tip", targetType: "approved_tip", targetId: tip.id, details: tip.title_en });

  return { ok: true, errors: {}, tip };
}

export function updateV2ApprovedTip(tipId, input) {
  const { validation, payload } = prepareApprovedTipPayload(input);
  if (!validation.valid) return { ok: false, errors: validation.errors, tip: null };

  let updatedTip = null;
  const nextTips = loadV2ApprovedTips({ includeInactive: true }).map((tip) => {
    if (tip.id !== tipId) return tip;

    updatedTip = {
      ...tip,
      ...payload,
      id: tip.id,
      created_at: tip.created_at,
      updated_at: new Date().toISOString(),
    };
    return updatedTip;
  });

  if (!updatedTip) return { ok: false, errors: { id: "Tip was not found." }, tip: null };

  saveV2ApprovedTips(nextTips);
  createV2AdminAuditLog({ action: "update tip", targetType: "approved_tip", targetId: updatedTip.id, details: updatedTip.title_en });
  return { ok: true, errors: {}, tip: updatedTip };
}

export function deleteV2ApprovedTip(tipId) {
  const tips = loadV2ApprovedTips({ includeInactive: true });
  const nextTips = tips.filter((tip) => tip.id !== tipId);
  if (nextTips.length === tips.length) return { ok: false, deletedId: null };

  saveV2ApprovedTips(nextTips);
  createV2AdminAuditLog({ action: "delete tip", targetType: "approved_tip", targetId: tipId, details: "" });
  return { ok: true, deletedId: tipId };
}

export function loadV2Quotes({ includeInactive = false } = {}) {
  const savedQuotes = getV2GuestCollection(V2_STORAGE_KEYS.quotes);
  const quotes = savedQuotes.length > 0 ? savedQuotes.map(normalizeQuote) : getDefaultQuotes();
  return includeInactive ? quotes : quotes.filter((quote) => quote.active !== false);
}

export function saveV2Quotes(quotes) {
  return saveV2GuestCollection(V2_STORAGE_KEYS.quotes, Array.isArray(quotes) ? quotes.map(normalizeQuote) : []);
}

export function createV2Quote(input) {
  const { validation, payload } = prepareQuotePayload(input);
  if (!validation.valid) return { ok: false, errors: validation.errors, quote: null };

  const now = new Date().toISOString();
  const quote = {
    ...payload,
    id: createLocalId("quote"),
    created_at: now,
    updated_at: now,
  };
  saveV2Quotes([quote, ...loadV2Quotes({ includeInactive: true })]);
  createV2AdminAuditLog({ action: "add quote", targetType: "quote", targetId: quote.id, details: quote.quote_en });

  return { ok: true, errors: {}, quote };
}

export function updateV2Quote(quoteId, input) {
  const { validation, payload } = prepareQuotePayload(input);
  if (!validation.valid) return { ok: false, errors: validation.errors, quote: null };

  let updatedQuote = null;
  const nextQuotes = loadV2Quotes({ includeInactive: true }).map((quote) => {
    if (quote.id !== quoteId) return quote;

    updatedQuote = {
      ...quote,
      ...payload,
      id: quote.id,
      created_at: quote.created_at,
      updated_at: new Date().toISOString(),
    };
    return updatedQuote;
  });

  if (!updatedQuote) return { ok: false, errors: { id: "Quote was not found." }, quote: null };

  saveV2Quotes(nextQuotes);
  createV2AdminAuditLog({ action: "update quote", targetType: "quote", targetId: updatedQuote.id, details: updatedQuote.quote_en });
  return { ok: true, errors: {}, quote: updatedQuote };
}

export function deleteV2Quote(quoteId) {
  const quotes = loadV2Quotes({ includeInactive: true });
  const nextQuotes = quotes.filter((quote) => quote.id !== quoteId);
  if (nextQuotes.length === quotes.length) return { ok: false, deletedId: null };

  saveV2Quotes(nextQuotes);
  createV2AdminAuditLog({ action: "delete quote", targetType: "quote", targetId: quoteId, details: "" });
  return { ok: true, deletedId: quoteId };
}

export function getV2DashboardQuote(date = new Date()) {
  const quotes = loadV2Quotes();
  if (quotes.length === 0) return getDefaultQuotes()[0];

  const dateKey = formatDateKey(date);
  const exactQuote = quotes.find((quote) => quote.display_date === dateKey);
  if (exactQuote) return exactQuote;

  const dayNumber = Math.floor(date.getTime() / 86400000);
  return quotes[Math.abs(dayNumber) % quotes.length];
}

export function loadV2KnowledgeItems({ includeInactive = true } = {}) {
  const items = getV2GuestCollection(V2_STORAGE_KEYS.knowledgeItems).map(normalizeKnowledgeItem);
  return includeInactive ? items : items.filter((item) => item.active !== false && item.reviewed);
}

export function saveV2KnowledgeItems(items) {
  return saveV2GuestCollection(V2_STORAGE_KEYS.knowledgeItems, Array.isArray(items) ? items.map(normalizeKnowledgeItem) : []);
}

export function createV2KnowledgeItem(input) {
  const { validation, payload } = prepareKnowledgeItemPayload(input);
  if (!validation.valid) return { ok: false, errors: validation.errors, item: null };

  const now = new Date().toISOString();
  const item = {
    ...payload,
    id: createLocalId("knowledge"),
    created_at: now,
    updated_at: now,
  };
  saveV2KnowledgeItems([item, ...loadV2KnowledgeItems()]);
  createV2AdminAuditLog({ action: "add knowledge item", targetType: "knowledge_item", targetId: item.id, details: item.title });

  return { ok: true, errors: {}, item };
}

export function updateV2KnowledgeItem(itemId, input) {
  const { validation, payload } = prepareKnowledgeItemPayload(input);
  if (!validation.valid) return { ok: false, errors: validation.errors, item: null };

  let updatedItem = null;
  const nextItems = loadV2KnowledgeItems().map((item) => {
    if (item.id !== itemId) return item;

    updatedItem = {
      ...item,
      ...payload,
      id: item.id,
      created_at: item.created_at,
      updated_at: new Date().toISOString(),
    };
    return updatedItem;
  });

  if (!updatedItem) return { ok: false, errors: { id: "Knowledge item was not found." }, item: null };

  saveV2KnowledgeItems(nextItems);
  createV2AdminAuditLog({ action: "update knowledge item", targetType: "knowledge_item", targetId: updatedItem.id, details: updatedItem.title });
  return { ok: true, errors: {}, item: updatedItem };
}

export function deleteV2KnowledgeItem(itemId) {
  const items = loadV2KnowledgeItems();
  const nextItems = items.filter((item) => item.id !== itemId);
  if (nextItems.length === items.length) return { ok: false, deletedId: null };

  saveV2KnowledgeItems(nextItems);
  createV2AdminAuditLog({ action: "delete knowledge item", targetType: "knowledge_item", targetId: itemId, details: "" });
  return { ok: true, deletedId: itemId };
}

export function loadV2AdminAppSettings() {
  return mergeAdminAppSettings(readV2Json(V2_STORAGE_KEYS.appSettings, V2_DEFAULT_ADMIN_APP_SETTINGS));
}

export function saveV2AdminAppSettings(settings) {
  const merged = mergeAdminAppSettings(settings);
  writeV2Json(V2_STORAGE_KEYS.appSettings, merged);
  createV2AdminAuditLog({ action: "update app setting", targetType: "app_settings", targetId: "local", details: "Admin app settings updated" });
  return merged;
}

export function loadV2AdminAuditLogs() {
  return getV2GuestCollection(V2_STORAGE_KEYS.adminAuditLogs).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}

export function createV2AdminAuditLog(input) {
  const entry = createAuditLogEntry(input);
  if (!entry.action || !entry.target_type) {
    return { ok: false, errors: { action: "Audit action and target type are required." }, log: null };
  }

  const log = {
    ...entry,
    id: createLocalId("audit"),
    created_at: new Date().toISOString(),
  };
  writeV2Json(V2_STORAGE_KEYS.adminAuditLogs, [log, ...loadV2AdminAuditLogs()]);
  return { ok: true, errors: {}, log };
}

export function getV2GuestCollection(key) {
  const value = readV2Json(key, []);
  return Array.isArray(value) ? value : [];
}

export function saveV2GuestCollection(key, items) {
  const safeItems = Array.isArray(items) ? items : [];
  return writeV2Json(key, safeItems);
}

export function getV2GuestProfileSettings() {
  const savedSettings = loadV2Settings();

  return {
    profile: savedSettings.profile,
    daily_targets: savedSettings.daily_targets,
  };
}

export function clearV2GuestData() {
  [
    V2_STORAGE_KEYS.session,
    V2_STORAGE_KEYS.settings,
    V2_STORAGE_KEYS.guestCustomFoods,
    V2_STORAGE_KEYS.guestFoodLogs,
    V2_STORAGE_KEYS.guestMedicationReminders,
    V2_STORAGE_KEYS.guestReminderEvents,
  ].forEach(removeV2Json);
  removeV2Json(V2_LEGACY_STORAGE_KEYS.guestMode);
  removeV2Json(V2_LEGACY_STORAGE_KEYS.guestProfile);
}

function getCustomFoodStorageKey(isGuest) {
  return isGuest ? V2_STORAGE_KEYS.guestCustomFoods : V2_STORAGE_KEYS.customFoods;
}

function normalizeAdminMainFood(food = {}) {
  return {
    ...food,
    name_en: String(food.name_en || food.name || "").trim(),
    name_ar: String(food.name_ar || "").trim(),
    category: String(food.category || "Uncategorized").trim(),
    serving_quantity: Number(food.serving_quantity) || 1,
    serving_unit: String(food.serving_unit || "serving").trim(),
    potassium_mg: Number(food.potassium_mg) || 0,
    phosphorus_mg: Number(food.phosphorus_mg) || 0,
    sodium_mg: Number(food.sodium_mg) || 0,
    protein_g: Number(food.protein_g) || 0,
    source: String(food.source || "Local food list").trim(),
    verified: food.verified !== false,
    active: food.active !== false,
    icon_key: food.icon_key || "food",
  };
}

function normalizeApprovedTip(tip = {}) {
  return {
    ...tip,
    title_en: String(tip.title_en || tip.title || "").trim(),
    title_ar: String(tip.title_ar || "").trim(),
    body_en: String(tip.body_en || tip.body || "").trim(),
    body_ar: String(tip.body_ar || "").trim(),
    category: String(tip.category || "General").trim(),
    category_ar: String(tip.category_ar || "").trim(),
    active: tip.active !== false,
    reviewed: tip.reviewed !== false,
  };
}

function normalizeQuote(quote = {}) {
  return {
    ...quote,
    quote_en: String(quote.quote_en || quote.quote || "").trim(),
    quote_ar: String(quote.quote_ar || "").trim(),
    active: quote.active !== false,
    display_date: String(quote.display_date || "").trim(),
  };
}

function normalizeKnowledgeItem(item = {}) {
  return {
    ...item,
    title: String(item.title || "").trim(),
    type: String(item.type || "manual_note").trim(),
    language: String(item.language || "en").trim(),
    category: String(item.category || "General").trim(),
    active: item.active !== false,
    reviewed: Boolean(item.reviewed),
    source_notes: String(item.source_notes || "").trim(),
    content: String(item.content || "").trim(),
    file_name: String(item.file_name || "").trim(),
  };
}

function getDefaultApprovedTips() {
  return APPROVED_DASHBOARD_TIPS.map((tip) =>
    normalizeApprovedTip({
      id: tip.id,
      title_en: tip.title,
      title_ar: tip.title_ar,
      body_en: tip.body,
      body_ar: tip.body_ar,
      category: tip.category,
      category_ar: tip.category_ar,
      active: true,
      reviewed: true,
    })
  );
}

function getDefaultQuotes() {
  return DASHBOARD_QUOTES.map((quote) =>
    normalizeQuote({
      id: quote.id,
      quote_en: quote.quote,
      quote_ar: quote.quote_ar,
      active: true,
      display_date: "",
    })
  );
}

function formatDateKey(date = new Date()) {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getReminderStorageKey(isGuest) {
  return isGuest ? V2_STORAGE_KEYS.guestMedicationReminders : V2_STORAGE_KEYS.medicationReminders;
}

function getReminderEventStorageKey(isGuest) {
  return isGuest ? V2_STORAGE_KEYS.guestReminderEvents : V2_STORAGE_KEYS.reminderEvents;
}

function normalizeStoredReminder(reminder = {}) {
  const timesPerDay = normalizeTimesPerDay(reminder.times_per_day);
  const reminderTimes = getReminderTimes({
    ...reminder,
    reminder_times:
      Array.isArray(reminder.reminder_times) && reminder.reminder_times.length > 0
        ? reminder.reminder_times.slice(0, timesPerDay)
        : [reminder.reminder_time || "19:30"],
  });

  return {
    ...reminder,
    medication_name: String(reminder.medication_name || "").trim(),
    dose: String(reminder.dose || "").trim(),
    times_per_day: Math.min(timesPerDay, reminderTimes.length || timesPerDay),
    reminder_times: reminderTimes.length > 0 ? reminderTimes : ["19:30"],
    reminder_time: reminderTimes[0] || String(reminder.reminder_time || "19:30").trim(),
    frequency: reminder.frequency || "daily",
    specific_days: Array.isArray(reminder.specific_days) ? reminder.specific_days : [],
    once_date: String(reminder.once_date || "").trim(),
    active: reminder.active !== false,
    channel: isAllowed(reminder.channel, V2_ALLOWED_REMINDER_CHANNELS, V2_DEFAULT_REMINDERS.channel),
  };
}

function sortRemindersByTime(a, b) {
  return String(a.reminder_time || "").localeCompare(String(b.reminder_time || ""));
}

function createLocalId(prefix) {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return `${prefix}-${globalThis.crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
